import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Button } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { ResetIcon, UserSuccessIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { uncompletedLoanErrorMessageParams } from 'app/pages/loanApplication/LoanApplication.constants';
import clsx from 'clsx';
import dayjs from 'dayjs';
import _ from 'lodash';
import { nanoid } from 'nanoid';

import { IconSize } from 'constants/coreDesignSystem';
import { PAGE_CODES } from 'constants/pageCodes';
import { sortArrayByDate } from 'utils/data';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import { showMessageThatCurrentUserCantChangeLoanAction } from 'store/loanApplications/actions';
import {
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanInitiatedWithErrors,
} from 'store/loanApplications/selectors';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { defaultInitialEntitySideData, pagesPropTypes } from '../MainContent.constants';
import { BadgesWrapper, ContentHeadline } from '../sharedComponents';

import LoanTag from './LoanTag';
import LoanTagsAssignmentForm from './LoanTagAssignmentForm';
import LoanTagsHistory from './LoanTagsHistory';

const LoanTags = ({
  isLoading,
  isSaving,
  className,
  applicationData,
  initialEntitySideData = defaultInitialEntitySideData,
  entitySideData,
  setInitialEntitySideData,
  setEntitySideData,
}) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const isLoanInitiatedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const { isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_TAG_ASSIGNEMENT),
  );

  const { loanTags: savedLoanTags, initiallyNewAddedLoanTags } = applicationData;
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  const { loanTags = [] } = entitySideData;
  const isDataLoading = isLoading && !isSaving;

  useEffect(() => {
    if (initialEntitySideData.loanTags || !savedLoanTags) {
      return;
    }

    const initialLoanTagsData = {
      loanTags: savedLoanTags,
      newAddedLoanTags: [],
      deletedLoanTags: [],
      annihilatedLoanTags: [],
    };

    setEntitySideData(prev => ({
      ...prev,
      ...initialLoanTagsData,
    }));
    setInitialEntitySideData(prev => ({
      ...prev,
      ...initialLoanTagsData,
    }));
  }, [
    initialEntitySideData,
    applicationData,
    savedLoanTags,
    setEntitySideData,
    setInitialEntitySideData,
  ]);

  const onDeleteLoanTag = useCallback(
    e => {
      const { tagId: tagIdToDelete } = e.currentTarget.dataset;

      setEntitySideData(prev => {
        const { newAddedLoanTags, deletedLoanTags, loanTags, annihilatedLoanTags } = prev;

        const isNewAdded = newAddedLoanTags.includes(tagIdToDelete);
        const isInitiallyNewAdded = initiallyNewAddedLoanTags.includes(tagIdToDelete);

        const updatedNewAddedLoanTags = isNewAdded
          ? _.without(newAddedLoanTags, tagIdToDelete)
          : newAddedLoanTags;

        const updatedDeletedLoanTags = isNewAdded
          ? deletedLoanTags
          : [...deletedLoanTags, tagIdToDelete];

        const updatedAnnihilatedLoanTags = isInitiallyNewAdded
          ? [...annihilatedLoanTags, tagIdToDelete]
          : annihilatedLoanTags;

        const updatedLoanTags = loanTags.reduce((acc, loanTag) => {
          const { isNewAdded, isInitiallyNewAdded, tagId } = loanTag;

          if (tagIdToDelete !== tagId) {
            acc.push(loanTag);
          }

          if (tagIdToDelete === tagId && !isNewAdded && !isInitiallyNewAdded) {
            acc.push({
              ...loanTag,
              isDeleted: true,
            });
          }

          return acc;
        }, []);

        return {
          ...prev,
          annihilatedLoanTags: updatedAnnihilatedLoanTags,
          newAddedLoanTags: updatedNewAddedLoanTags,
          deletedLoanTags: updatedDeletedLoanTags,
          loanTags: updatedLoanTags,
        };
      });
    },
    [setEntitySideData, initiallyNewAddedLoanTags],
  );

  const onRestoreLoanTag = useCallback(
    e => {
      const { tagId: tagIdToRestore } = e.currentTarget.dataset;

      setEntitySideData(prev => {
        const { loanTags, deletedLoanTags } = prev;

        const updatedDeletedLoanTags = _.without(deletedLoanTags, tagIdToRestore);

        const updatedLoanTags = loanTags.map(loanTag =>
          loanTag.tagId === tagIdToRestore ? { ...loanTag, isDeleted: false } : loanTag,
        );

        return { ...prev, loanTags: updatedLoanTags, deletedLoanTags: updatedDeletedLoanTags };
      });
    },
    [setEntitySideData],
  );

  const onAssignLoanTag = useCallback(
    loanTagData => {
      const { id: tagIdToAssign, name: tagName } = loanTagData;

      setEntitySideData(prev => {
        const { newAddedLoanTags, loanTags, annihilatedLoanTags, deletedLoanTags } = prev;

        const isAnnihilated = annihilatedLoanTags.includes(tagIdToAssign);

        const loanTagToAssign = isAnnihilated
          ? savedLoanTags.find(({ tagId }) => tagId === tagIdToAssign)
          : {
              tagId: tagIdToAssign,
              tagName,
              assignedDate: dayjs(),
              isNewAdded: true,
            };

        const updatedLoanTags = [loanTagToAssign, ...loanTags];

        const sortedUpdatedLoanTags = isAnnihilated
          ? sortArrayByDate(updatedLoanTags, 'assignedDate', 'desc')
          : updatedLoanTags;

        const updatedNewAddedLoanTags = isAnnihilated
          ? newAddedLoanTags
          : [...newAddedLoanTags, tagIdToAssign];

        const updatedDeletedLoanTags = isAnnihilated
          ? _.without(deletedLoanTags, tagIdToAssign)
          : deletedLoanTags;

        const updatedAnnihilatedLoanTags = isAnnihilated
          ? _.without(annihilatedLoanTags, tagIdToAssign)
          : annihilatedLoanTags;

        return {
          ...prev,
          loanTags: sortedUpdatedLoanTags,
          newAddedLoanTags: updatedNewAddedLoanTags,
          deletedLoanTags: updatedDeletedLoanTags,
          annihilatedLoanTags: updatedAnnihilatedLoanTags,
        };
      });
    },
    [setEntitySideData, savedLoanTags],
  );

  const onOpenLoanTagsHistory = useCallback(() => {
    const dialogId = nanoid();

    dispatch(
      openDialogAction({
        id: dialogId,
        isRestyled: true,
        header: 'Loan Tags History',
        size: ModalSize.LARGE,
        hasScrollY: false,
        body: () => <LoanTagsHistory />,
      }),
    );
  }, [dispatch]);

  const onOpenAssignLoanTagForm = useCallback(() => {
    const dialogId = nanoid();

    if (isLoanInitiatedWithErrors) {
      return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
    }

    if (!isCurrentUserCanChangeLoan) {
      return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
    }

    const loanTagsToExclude = loanTags.map(({ tagId }) => tagId);

    dispatch(
      openDialogAction({
        id: dialogId,
        isRestyled: true,
        header: 'Loan Tag Assignment',
        hasScrollY: false,
        body: dialogProps => (
          <LoanTagsAssignmentForm
            {...dialogProps}
            applicationId={applicationId}
            dialogId={dialogId}
            loanTagsToExclude={loanTagsToExclude}
            onAssignLoanTag={onAssignLoanTag}
          />
        ),
      }),
    );
  }, [
    dispatch,
    applicationId,
    isCurrentUserCanChangeLoan,
    isLoanInitiatedWithErrors,
    loanTags,
    onAssignLoanTag,
  ]);

  return (
    <Box className={clsx('overflow-x-hidden', className)}>
      <ContentHeadline
        controls={
          <ButtonGroupWrapper className="items-center">
            <Button
              data-testid={PropertyAndLoanTestIds.LOAN_TAGS_HISTORY_BUTTON}
              disabled={isLoading}
              size="small"
              startIcon={<ResetIcon size={IconSize[16]} />}
              variant="outlined"
              onClick={onOpenLoanTagsHistory}
            >
              History
            </Button>
            {isManageAllowed && (
              <Button
                data-testid={PropertyAndLoanTestIds.LOAN_TAGS_ASSIGN_BUTTON}
                disabled={isLoading}
                size="small"
                startIcon={<UserSuccessIcon size={IconSize[16]} />}
                variant="outlined"
                onClick={onOpenAssignLoanTagForm}
              >
                Assign
              </Button>
            )}
          </ButtonGroupWrapper>
        }
        title="Loan Tags"
      />

      <BadgesWrapper data={loanTags} isDataLoading={isDataLoading} skeletonHeight={74}>
        {loanTags.map(loanTag => (
          <LoanTag
            isControlVisible={isManageAllowed}
            isLoading={isLoading}
            key={loanTag.tagId}
            loanTag={loanTag}
            onDeleteLoanTag={onDeleteLoanTag}
            onRestoreLoanTag={onRestoreLoanTag}
          />
        ))}
      </BadgesWrapper>
    </Box>
  );
};

LoanTags.propTypes = pagesPropTypes;

export default memo(LoanTags);
